import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Button,
  Drawer,
  Input,
  List,
  Avatar,
  Space,
  Typography,
  message,
  Badge,
} from "antd";
import {
  MessageOutlined,
  SendOutlined,
  RobotOutlined,
  UserOutlined,
} from "@ant-design/icons";
import groqService from "../utils/groqService";
import { AppContext } from "../context/AppContext";
import "./AIChatbot.css";

const { TextArea } = Input;
const { Text } = Typography;

const AIChatbot = () => {
  const { user, company } = useContext(AppContext);
  const [visible, setVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const userName = user?.first_name || user?.name || "there";
      setMessages([
        {
          role: "assistant",
          content: `Hi ${userName}! 👋 I'm your KwachaPlus AI assistant. I can help you with:

• Understanding loan processes and calculations
• Navigating the system
• Answering questions about features
• Providing guidance on common tasks
• Explaining loan terms and fees

What would you like to know?`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [user]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setLoading(true);

    try {
      // Prepare conversation history for API (only send role and content)
      const conversationHistory = [...messages, userMessage].map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await groqService.chat(conversationHistory);

      const assistantMessage = {
        role: "assistant",
        content: response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Show badge if drawer is closed
      if (!visible) {
        setHasNewMessage(true);
      }
    } catch (error) {
      console.error("Chat error:", error);
      message.error(error.message || "Failed to get AI response");

      const errorMessage = {
        role: "assistant",
        content:
          "I apologize, but I encountered an error. Please make sure the Groq API is properly configured.",
        timestamp: new Date(),
        isError: true,
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleDrawer = () => {
    setVisible(!visible);
    if (!visible) {
      setHasNewMessage(false);
    }
  };

  const clearChat = () => {
    const userName = user?.first_name || user?.name || "there";
    setMessages([
      {
        role: "assistant",
        content: `Chat cleared. Hi ${userName}! I'm your KwachaPlus AI assistant. How can I help you today?`,
        timestamp: new Date(),
      },
    ]);
  };

  return (
    <>
      {/* Floating Chat Button - hide when drawer is open */}
      {!visible && (
        <div className="ai-chatbot-float-button-container">
          <Badge dot={hasNewMessage} offset={[-5, 5]}>
            <Button
              className="ai-chatbot-float-button"
              type="primary"
              shape="circle"
              size="large"
              icon={<MessageOutlined style={{ fontSize: "24px" }} />}
              onClick={toggleDrawer}
            />
          </Badge>
        </div>
      )}

      {/* Chat Drawer */}
      <Drawer
        title={
          <Space>
            <RobotOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
            <span>KwachaPlus AI Assistant</span>
          </Space>
        }
        placement="right"
        width={400}
        onClose={toggleDrawer}
        open={visible}
        zIndex={9999}
        extra={
          <Button size="small" onClick={clearChat}>
            Clear Chat
          </Button>
        }
        bodyStyle={{ padding: 0, display: "flex", flexDirection: "column" }}
      >
        {/* Messages Container */}
        <div className="ai-chatbot-messages" ref={chatContainerRef}>
          <List
            dataSource={messages}
            renderItem={(msg) => (
              <List.Item
                className={`ai-chatbot-message ${
                  msg.role === "user" ? "user-message" : "assistant-message"
                }`}
                style={{
                  border: "none",
                  padding: "12px 16px",
                }}
              >
                <List.Item.Meta
                  avatar={
                    msg.role === "user" ? (
                      <Avatar
                        icon={<UserOutlined />}
                        style={{
                          backgroundColor: "#1890ff",
                        }}
                      />
                    ) : (
                      <Avatar
                        src={
                          company?.logo || "/images/android-chrome-192x192.png"
                        }
                        style={{
                          backgroundColor: "white",
                        }}
                      />
                    )
                  }
                  description={
                    <div>
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {msg.content}
                      </div>
                      <Text
                        type="secondary"
                        style={{ fontSize: "11px", marginTop: "4px" }}
                      >
                        {msg.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="ai-chatbot-input-container">
          <Space.Compact style={{ width: "100%" }}>
            <TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything about KwachaPlus..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={loading}
              style={{ resize: "none" }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              loading={loading}
              disabled={!inputValue.trim()}
            >
              Send
            </Button>
          </Space.Compact>
        </div>
      </Drawer>
    </>
  );
};

export default AIChatbot;
